import { newCorrelationId } from "@/lib/correlation";

// SPA-side diagnostic event contract (ticket #123). Mirrors the AUTHORITATIVE
// BFF validator at apps/bff/src/routes/diagnostics/schema.ts — the two apps
// share no package, so keep the codes/bounds in sync. The BFF re-validates
// strictly and rejects anything off-contract, so this side is a best-effort
// producer that clamps obviously-oversized fields before delivery.

export const SCHEMA_VERSION = 1 as const;

export type DiagnosticLevel = "warn" | "error";
export type DiagnosticCategory = "runtime" | "application" | "graphql" | "http" | "websocket";

export const CODES = {
  runtime: {
    UNCAUGHT_ERROR: "runtime.uncaught_error",
    UNHANDLED_REJECTION: "runtime.unhandled_rejection",
  },
  application: {
    OPERATION_FAILURE: "application.operation_failure",
    MUTATION_FAILURE: "application.mutation_failure",
    QUERY_FAILURE: "application.query_failure",
    SUBSCRIPTION_FAILURE: "application.subscription_failure",
    AUTOSAVE_FAILURE: "application.autosave_failure",
    // BFF-emitted only. POST /diagnostics requires a live session, so an
    // expired session cannot deliver its own expiry event; the BFF logs it at
    // the detection point instead. Mirrored here to keep the namespace closed.
    SESSION_EXPIRED: "application.session_expired",
  },
  graphql: {
    TRANSPORT_FAILURE: "graphql.transport_failure",
    OPERATION_ERROR: "graphql.operation_error",
  },
  http: {
    REQUEST_FAILURE: "http.request_failure",
    STREAM_FAILURE: "http.stream_failure",
  },
  websocket: {
    CONNECTION_ERROR: "websocket.connection_error",
    CLOSED: "websocket.closed",
  },
} as const;

// Field bounds — mirror the BFF.
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 4000;
const MAX_ERROR_NAME_LEN = 200;
const MAX_ROUTE_LEN = 200;
const MAX_BUILD_VERSION_LEN = 100;
const MAX_OPERATION_NAME_LEN = 200;
const MAX_ENDPOINT_LEN = 200;
const MAX_CODE_LEN = 200;

export interface DiagnosticInput {
  level: DiagnosticLevel;
  code: string;
  category: DiagnosticCategory;
  message?: string;
  correlationId?: string;
  details?: Record<string, unknown>;
}

export interface SerializedEvent {
  schemaVersion: 1;
  eventId: string;
  occurredAt: string;
  level: DiagnosticLevel;
  code: string;
  category: DiagnosticCategory;
  message?: string;
  correlationId?: string;
  route?: string;
  buildVersion?: string;
  details?: Record<string, unknown>;
}

export interface BuildContext {
  route?: string;
  buildVersion?: string;
  eventId?: string;
  occurredAt?: string;
}

function clamp(v: unknown, max: number): string | undefined {
  if (typeof v !== "string") return undefined;
  return v.length > max ? v.slice(0, max) : v;
}

// Clamp the bounded string fields per category so an unexpectedly large stack
// or operation name can't blow the batch size. Unknown keys are passed through
// (the BFF strips/rejects them); we only defend length here.
function clampDetails(
  category: DiagnosticCategory,
  details: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...details };
  const clampKey = (key: string, max: number) => {
    if (typeof out[key] === "string") out[key] = clamp(out[key], max);
  };
  if (category === "runtime") {
    clampKey("errorName", MAX_ERROR_NAME_LEN);
    clampKey("stack", MAX_STACK_LEN);
  } else if (category === "graphql" || category === "websocket") {
    clampKey("operationName", MAX_OPERATION_NAME_LEN);
    clampKey("code", MAX_CODE_LEN);
    clampKey("classification", MAX_CODE_LEN);
  } else if (category === "http") {
    clampKey("endpoint", MAX_ENDPOINT_LEN);
  }
  return out;
}

// Build the wire event from a producer input plus runtime context. Never
// throws — a producer's logging call must be non-fatal.
export function buildEvent(input: DiagnosticInput, ctx: BuildContext = {}): SerializedEvent {
  const event: SerializedEvent = {
    schemaVersion: SCHEMA_VERSION,
    eventId: ctx.eventId ?? newCorrelationId(),
    occurredAt: ctx.occurredAt ?? new Date().toISOString(),
    level: input.level,
    code: input.code,
    category: input.category,
  };
  const message = clamp(input.message, MAX_MESSAGE_LEN);
  if (message !== undefined) event.message = message;
  if (input.correlationId !== undefined) event.correlationId = input.correlationId;
  const route = clamp(ctx.route, MAX_ROUTE_LEN);
  if (route !== undefined) event.route = route;
  const buildVersion = clamp(ctx.buildVersion, MAX_BUILD_VERSION_LEN);
  if (buildVersion !== undefined) event.buildVersion = buildVersion;
  if (input.details && Object.keys(input.details).length > 0) {
    event.details = clampDetails(input.category, input.details);
  }
  return event;
}

// Serialized byte length used by the queue/batcher caps.
export function eventBytes(event: SerializedEvent): number {
  return JSON.stringify(event).length;
}
